import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import Backend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

import { stripDoNotTranslateMarkers } from './utils/i18n.utils';

/**
 * i18next postProcessor that strips the do-not-translate marker `[[literal]]` down to its
 * bare literal text `literal`.
 *
 * Authors wrap proper nouns / service names that must never be translated in double
 * square brackets in `public/locales/en/translation.json`, e.g. `Welcome to [[AWS Jam]]`.
 * The offline translation script (scripts/localize_clean.js) removes these brackets when
 * it generates the non-English locale files, but the English source is served verbatim at
 * runtime and never passes through that script. This postProcessor closes that gap so the
 * markers are stripped for the English locale (and defensively for every locale) at t()
 * time. It runs after interpolation, so `{{var}}` values are already substituted and are
 * left untouched.
 */
const doNotTranslatePostProcessor = {
  type: 'postProcessor' as const,
  name: 'stripDoNotTranslate',
  process: (value: string) => stripDoNotTranslateMarkers(value),
};

void i18n
  // load translation using http -> see /public/locales
  .use(Backend)
  // detect user language
  .use(LanguageDetector)
  // strip [[do-not-translate]] markers from rendered strings
  .use(doNotTranslatePostProcessor)
  // pass the i18n instance to react-i18next.
  .use(initReactI18next)
  // init i18next
  .init({
    fallbackLng: 'en',
    debug: true,

    // Apply the do-not-translate marker stripping to every rendered string.
    postProcess: [doNotTranslatePostProcessor.name],

    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
  });

export default i18n;
